(ns hai.account.refer.routes
  "Refer a friend — pick people from your own contact book (or type an address)
  and each gets an invite email.

  The picker is the sharing dialog's contacts field, reused wholesale: same
  dropdown over `:contacts/table`, same typed-in-email parsing, same pills. Only
  the wording and what happens on submit differ."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as contacts]
   [hai.sharing.components :as sharing]
   [hai.ui.components :as c]
   [hai.ui.components.form.core :as form]
   [hai.ui.components.form.select.components :as select.components]
   [hai.ui.components.form.select.searchable-multi-select :as searchable-multi-select]
   [hai.ui.components.new-design.button :as button]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent-fx defsub]]
   [hai.web-app.layout :as layout]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]))

(def icon icons/outlined-card_giftcard)

(defn- ->recipient-label
  [scope]
  (or (not-empty (:contact/full-name scope))
      (first (:contact/email scope))))

(defn- ->recipient-list
  "`Ana`, `Ana and Ben`, `Ana, Ben and Chris` — no Oxford comma, per the
  language guidelines."
  [scopes]
  (let [names (into [] (keep ->recipient-label) scopes)]
    (if (< (count names) 2)
      (first names)
      (str (str/join ", " (butlast names)) " and " (last names)))))

(defsub ::sent-to [db] (::sent-to db))

(defevent-fx ::sent
  [{:keys [db]} scopes]
  {:db (assoc db ::sent-to (->recipient-list scopes))})

(defevent-fx ::send
  [{:keys [db]} ?form]
  (let [scopes-form-id [(form/->id ?form) :scopes]
        ;; Merge committed pills with any address still sitting uncommitted in
        ;; the input, so clicking Send doesn't silently drop what was typed --
        ;; the same reason `sharing/::on-share` does this.
        scopes (into (vec (form/->field-current-value db ?form :scopes))
                     (sharing/extract-scopes-from-select db scopes-form-id))]
    (when (seq scopes)
      {:dispatch [:resolve
                  [(list 'referral/invite {:scopes (mapv sharing/->share scopes)})]
                  {:on-success
                   (fn [_]
                     (>e [::sent scopes])
                     (>e [:form.field/clear! scopes-form-id :table/search])
                     (>e [:form.field/set-value! scopes-form-id :multi-select/selected []])
                     (>e [:form.field/set-value! ?form :scopes []]))}]})))

(o/defstyled invite-form-render :div
  [:.scopes :flex :flex-wrap {:gap "0.5rem"} :w-100%]
  [:.actions :flex :items-center {:gap "0.5rem"} :mt-3]
  ([{:as form {:keys [scopes]} :form/fields}]
   [:<>
    [:div.scopes
     [form/collection (assoc scopes :form.field.collection/field sharing/user-pill)]
     [searchable-multi-select/field
      (assoc scopes
             :query [{:contacts/table [{:collection/items contacts/query}]}]
             :transform-selected sharing/drop-nil-full-name
             :render {:style select.components/radix
                      :label contacts/->label+email
                      :disabled-reason sharing/contact-disabled-reason
                      :field sharing/search-field})]]
    [:div.actions
     ;; TODO this should just be a `:disabled?` flag -- same as sharing.
     (if @(<s [::sharing/valid? form])
       [button/dark-small
        {:on-click (fn [_] (>e [::send form]))
         :text "Send invite"}]
       [button/dark-small-disabled
        {:text "Send invite"}])]]))

(defn invite-form
  []
  (let [form-id ::invite-form]
    [form/component
     {:id form-id
      :schema [:map
               [:scopes
                {:form.field/placeholder "Add your friend's email"
                 :on-change (fn [_]
                              (>e [:form.field/clear! [form-id :scopes] :table/search]))}
                [:vector :scope]]]
      :render invite-form-render}]))

(defn confirmation
  []
  (when-let [name @(<s ::sent-to)]
    [c/Text
     (str "Thank you for being a kind human. Your gift of free access is on its way to "
          name ".")]))

(defn page
  [_]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/content
    [invite-form]
    [confirmation]]])

(defn routes []
  [["/account/refer" {:name :account/refer
                      ::routing/window-title "Refer a friend"
                      :label "Refer a friend"
                      :icon icon
                      :view page}]])
